import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { ChevronDown, ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, RefreshCw, Trash2, Activity, AlertCircle, ChevronsUpDown } from "lucide-react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { format, formatDistanceToNow } from "date-fns";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { buildApiUrl } from "../config/api";

// Define event interface based on your API structure
interface Event {
  id: string;
  name: string;
  tracked_at: string;
  metadata: Record<string, any>;
  project: {
    id: number;
    name: string;
  };
  organization: {
    id: number;
    name: string;
  };
}

interface Project {
  id: number;
  name: string;
  api_key: string;
  organization: {
    id: number;
    name: string;
  };
}

interface EventsProps {
  currentProject: Project | null;
  token: string;
}

const Events: React.FC<EventsProps> = ({ currentProject, token }) => {
  // Ref to track initial mount
  const isInitialMount = useRef(true);
  
  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Debounced search for API
  const [sorting, setSorting] = useState<SortingState>([{ id: "tracked_at", desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(25);
  const [pagination, setPagination] = useState<{
    count: number;
    page: number;
    items: number;
    pages: number;
    prev: number | null;
    next: number | null;
  } | null>(null);

  // Fetch events with server-side pagination and search
  const fetchEvents = async (page: number, search: string, signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);

      // Build URL with pagination and search params
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      
      if (currentProject) {
        params.append('project_id', currentProject.id.toString());
      }
      
      if (search) {
        params.append('search', search);
      }

      const url = `api/v1/events?${params.toString()}`;

      const response = await fetch(buildApiUrl(url), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();
      
      setEvents(data.data.events || []);
      setPagination(data.data.pagination || null);
    } catch (err: any) {
      // Ignore aborted requests
      if (err?.name === 'AbortError') return;
      console.error('Error fetching events:', err);
      setError('Failed to load events');
      setEvents([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch events when component mounts or when project changes
  useEffect(() => {
    const abortController = new AbortController();
    
    setCurrentPage(1);
    fetchEvents(1, searchQuery, abortController.signal);
    
    return () => abortController.abort();
  }, [currentProject?.id, token]);

  // Fetch when page changes (not on initial page 1)
  useEffect(() => {
    if (currentPage === 1) return;
    
    const abortController = new AbortController();
    fetchEvents(currentPage, searchQuery, abortController.signal);
    
    return () => abortController.abort();
  }, [currentPage]);

  // Debounce search input (skip on initial mount to avoid double fetch)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const abortController = new AbortController();
    
    const timer = setTimeout(() => {
      setSearchQuery(searchTerm);
      setCurrentPage(1);
      fetchEvents(1, searchTerm, abortController.signal);
    }, 500);

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [searchTerm]);

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchEvents(currentPage, searchQuery);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Define table columns
  const SortIcon: React.FC<{ isSorted: boolean; isAsc: boolean }> = ({ isSorted, isAsc }) => {
    if (!isSorted) return <ChevronsUpDown className="ml-2 h-4 w-4" />;
    return isAsc ? <ChevronUpIcon className="ml-2 h-4 w-4" /> : <ChevronDownIcon className="ml-2 h-4 w-4" />;
  };

  const columns = useMemo<ColumnDef<Event>[]>(() => [
    {
      id: "checkbox",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
          onCheckedChange={(value: boolean) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "expander",
      header: () => null,
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" onClick={() => row.toggleExpanded()}>
          {row.getIsExpanded() ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
        </Button>
      ),
    },
    {
      id: "tracked_at",
      accessorKey: "tracked_at",
      header: ({ column }) => (
        <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Date & Time <SortIcon isSorted={Boolean(column.getIsSorted())} isAsc={column.getIsSorted() === "asc"} />
        </Button>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue("tracked_at"));
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <div className="text-sm">
                  {formatDistanceToNow(date, { addSuffix: true }).replace('about ', '')}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {format(date, "MMM d, yyyy h:mm a")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Event <SortIcon isSorted={Boolean(column.getIsSorted())} isAsc={column.getIsSorted() === "asc"} />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("name")}</div>
      ),
    },
    {
      id: "project",
      accessorKey: "project.name",
      header: ({ column }) => (
        <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Project <SortIcon isSorted={Boolean(column.getIsSorted())} isAsc={column.getIsSorted() === "asc"} />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">{row.original.project.name}</div>
      ),
    },
    {
      id: "metadata_count",
      header: "Metadata",
      cell: ({ row }) => {
        const metadata = row.original.metadata || {};
        const count = Object.keys(metadata).length;
        return (
          <Badge variant="secondary" className="text-xs">
            {count} {count === 1 ? 'field' : 'fields'}
          </Badge>
        );
      },
    },
  ], []);

  // Create table instance (no client-side pagination/filtering)
  const table = useReactTable({
    data: events,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // Use server-side pagination
    pageCount: pagination?.pages || 0,
  });

  if (!currentProject) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No project selected</h3>
            <p className="text-sm">
              Please select a project from the sidebar to view its events
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4 text-neutral-800">
            <h1 className="text-2xl font-bold flex items-center"><Activity className="h-6 w-6 mr-1" /> Events</h1>
            <Badge variant="secondary" className="text-sm">
              {currentProject.name}
            </Badge>
            <Badge variant="outline" className="text-sm">
              {pagination?.count || 0} events
            </Badge>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Input
              placeholder="Search events..."
              value={searchTerm}
              onChange={handleSearch}
              className="max-w-md min-w-96"
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleRefresh} 
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Refresh</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={!Object.keys(rowSelection).length} variant="outline" size="sm">
                Actions <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem className="text-red-600">
                <Trash2 className="w-4 h-4 mr-2" /> Delete Selected
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div 
          className="rounded-md border"
          style={{
            opacity: 0,
            animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
          }}
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 mx-auto animate-spin mb-2" />
                    Loading events...
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <TableRow 
                      className="cursor-pointer hover:bg-muted/50"
                      style={{
                        opacity: 0,
                        animation: `tableRowFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${Math.min(index * 30, 600)}ms forwards`,
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow>
                        <TableCell colSpan={columns.length}>
                          <div className="p-4 bg-muted rounded-lg space-y-2">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-medium text-sm mb-2">Event Details</h4>
                                <div className="space-y-2">
                                  <div className="text-sm">
                                    <span className="font-medium">Event ID:</span> 
                                    <span className="font-mono ml-2">{row.original.id}</span>
                                  </div>
                                  <div className="text-sm">
                                    <span className="font-medium">Organization:</span> 
                                    <span className="ml-2">{row.original.organization.name}</span>
                                  </div>
                                  <div className="text-sm">
                                    <span className="font-medium">Tracked At:</span> 
                                    <span className="ml-2">{format(new Date(row.original.tracked_at), "PPpp")}</span>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <h4 className="font-medium text-sm mb-2">Metadata</h4>
                                <div className="space-y-1">
                                  {Object.entries(row.original.metadata || {}).map(([key, value]) => (
                                    <div key={key} className="text-sm">
                                      <span className="font-medium">{key}:</span> {String(value)}
                                    </div>
                                  ))}
                                  {Object.keys(row.original.metadata || {}).length === 0 && (
                                    <p className="text-sm text-muted-foreground">No metadata available</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No events found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        
        <div className="flex items-center justify-between space-x-2 py-4">
          <div className="text-sm text-muted-foreground">
            {pagination && (
              <>
                Showing {((pagination.page - 1) * pagination.items) + 1} to {Math.min(pagination.page * pagination.items, pagination.count)} of {pagination.count} events
              </>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={!pagination?.prev || loading}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              {pagination && (
                <>Page {pagination.page} of {pagination.pages}</>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={!pagination?.next || loading}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events; 